import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of, throwError, Observable, forkJoin } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { PeopleService } from './people.service';
import { EncodingService } from './encoding.service';

@Injectable({
  providedIn: 'root'
})
export class ChangeService {

  url = environment.apiConfig.baseUrl; // Remove /api suffix for base URL

  //  configID int, --autoint
  //  createdDateTime datetime,
  //  submittedDateTime dateTime,
  //  gmid nvarchar(12),
  //  submittedByGMID nvarchar(12),
  //  approvedByGMID nvarchar(12),
  //  approvedDateTime dateTime,
  //  crNumber nvarchar(32),
  //  itemDescription text, --nvarchar(max)
  //  cliConfig text,
  //  submissionStatus nvarchar(32),
  //  scheduledDateTime dateTime -- future use for automated config updates


  constructor(
    private _http: HttpClient,
    private _people: PeopleService,
    private encoding: EncodingService
  ) { }

  private encodeCliConfigForWrite(value: any): string {
    // Backend expects `cliConfig` to be a string for writes.
    // IMPORTANT: do NOT encode the JSON string itself (it would break quotes/brackets).
    // Instead, encode string *values* inside the cliConfig object/array and then stringify.
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return '[]';
      try {
        const parsed = JSON.parse(trimmed);
        return JSON.stringify(this.encoding.encodeObject(parsed));
      } catch {
        // If it's not valid JSON, keep it as a string but still encode its content.
        return this.encoding.encode(trimmed);
      }
    }

    try {
      return JSON.stringify(this.encoding.encodeObject(value ?? []));
    } catch {
      return '[]';
    }
  }

  private asCliConfigString(value: any): string {
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value ?? []);
    } catch {
      return '[]';
    }
  }

  private buildChangeWritePayload(change: any, overrides: Record<string, any> = {}): any {
    const src: any = change && typeof change === 'object' ? change : {};
    const out: any = {};

    // Only send fields that the backend is expected to persist. The UI enriches Change objects
    // with extra nested structures (e.g., createdBy) that can trigger Azure WAF false-positives.
    const allowedKeys = [
      'configID',
      'id',
      'createdDateTime',
      'submittedDateTime',
      'gmid',
      'submittedByGMID',
      'approvedByGMID',
      'approvedDateTime',
      'crNumber',
      'itemDescription',
      'cliConfig',
      'submissionStatus',
      'scheduledDateTime'
    ];

    for (const k of allowedKeys) {
      if (src[k] !== undefined) {
        out[k] = src[k];
      }
    }

    // Accept alternate casing from some backends/serializers.
    if (out.configID === undefined) {
      if (src.configId !== undefined) out.configID = src.configId;
      else if (src.config_id !== undefined) out.configID = src.config_id;
    }

    // Normalize the primary identifier fields.
    if (out.crNumber !== undefined) {
      out.crNumber = (out.crNumber ?? '').toString().trim();
    }

    // Note: some backend routes validate cliConfig strictly as a string.
    // We keep the value as-is here; individual write routes will coerce to string.
    if (out.cliConfig === undefined) {
      out.cliConfig = [];
    }

    // Apply explicit overrides last.
    for (const [k, v] of Object.entries(overrides)) {
      out[k] = v;
    }

    return out;
  }

  private normalizeCliConfig(item: any): void {
    if (!item) return;
    if (typeof item.cliConfig !== 'string') return;

    const raw = item.cliConfig;
    const trimmed = raw.trim();
    if (!trimmed) {
      item.cliConfig = [];
      return;
    }

    try {
      const parsed = JSON.parse(trimmed);
      item.cliConfig = this.encoding.decodeObject(parsed);
    } catch (e) {
      console.error('Failed to parse cliConfig; defaulting to [].', {
        configID: item?.configID,
        crNumber: item?.crNumber,
        error: e
      });
      item.cliConfig = [];
    }
  }

  /**
   * Fetches all active changes from the backend. That means any change with submissionStatus "Active".
   * @returns An Observable containing an array of active changes.
   */
  getActiveChanges() {
    console.log('Fetching active changes');
    return this._http.get(this.url + '/enac/change/all').pipe(
      map((res: any) => {
        const rows = Array.isArray(res) ? res : [];
        const change = rows.filter((item: any) => item.submissionStatus === "Active");
        change.forEach((item: any) => this.normalizeCliConfig(item));
        return change.length > 0 ? change : [];
      }),
      catchError((error) => {
        console.error('Error fetching active changes:', error);
        return throwError(() => error);
      })
    );
  }

  getAllChanges() {
    console.log('Fetching all changes');
    return this._http.get(this.url + '/enac/change/all').pipe(
      switchMap((res: any) => {
        const rows = Array.isArray(res) ? res : [];
        const changes = rows.length > 0 ? rows : [];

        // Parse cliConfig for each item (defensive; don't let a single bad row blank the page)
        changes.forEach((item: any) => this.normalizeCliConfig(item));
        
        // If no changes, return empty array
        if (changes.length === 0) {
          return of([]);
        }
        
        // Create an array of observables to fetch person data for each change
        const personLookups: Observable<any>[] = changes.map((item: any) => {
          if (item.gmid) {
            return this._people.getPersonByGMID(item.gmid).pipe(
              switchMap((people: any[]) => {
                const person = Array.isArray(people) && people.length > 0 ? { ...people[0] } : null;
                if (!person) {
                  item.createdBy = null;
                  return of(item);
                }

                person.gmid = person.onPremisesSamAccountName || person.gmid || item.gmid;

                // Photos are not displayed in the UI; avoid calling Graph `/photo/$value`.
                person.picture = null;
                item.createdBy = person;
                return of(item);
              }),
              catchError((error) => {
                console.error(`Failed to fetch person for GMID ${item.gmid}:`, error);
                item.createdBy = null;
                return of(item);
              })
            );
          } else {
            item.createdBy = null;
            return of(item);
          }
        });
        
        // Wait for all person lookups to complete
        return personLookups.length > 0 ? forkJoin(personLookups) : of(changes);
      }),
      map((changes: any) => {
        //console.log('All changes with createdBy data:', changes); // this map isnt 'needed' but is good for debugging, it will run after all the person lookups are done
        return changes;
      }),
      catchError((error) => {
        console.error('Error fetching all changes:', error);
        return throwError(() => error);
      })
    );
  }

  getChangeByCrNumber(crNumber: string, options?: { silentNotFound?: boolean }) {
    return this._http.get(`${this.url}/enac/change/cr/${crNumber}`).pipe(
      map((res: any) => {
        const rows = Array.isArray(res) ? res : [];
        if (rows.length > 0) {
          console.log('Change found for CR number:', crNumber, res, "selecting the first item");          
          this.normalizeCliConfig(rows[0]);
          return rows[0]; // Assuming the response is an array and we want the first item
        } else {
          if (!options?.silentNotFound) {
            console.info('No change found for CR number:', crNumber, res);
          }
          return null; // Or handle as needed
        }
      }),
      catchError((error) => {
        // Some backends return 404 when the CR does not exist; treat it as "not found".
        if (error?.status === 404) {
          return of(null);
        }

        console.error('Error fetching change by CR number:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Deletes a specific item from a change based on the provided configID and item.
   * @param id configID number of the change
   * @param item The item to be deleted from the change
   * @returns An Observable indicating the success or failure of the deletion operation.
   */
  deleteChangeItem(id: any, item: any): Observable<any> {
    if (!id || !item) {
      console.error('configID number or item is missing');
      return throwError(() => new Error('CR number or item is missing'));
    }
    return this._http.delete(`${this.url}/enac/change/${id}`).pipe(
      map((res: any) => res),
      catchError((error) => {
        console.error('Error deleting change item:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Creates a new change in the backend with the provided change object.
   * @param change The change object to be created. Requires a GMID.
   * @returns An Observable containing the created change or an error if the operation fails.
   */
  createNewChange(change: any) {
    console.log("Creating a new change", change);

    const trimmedCr = (change?.crNumber ?? '').toString().trim();
    if (!trimmedCr) {
      return throwError(() => new Error('CR_NUMBER_REQUIRED'));
    }

    const payload: any = this.buildChangeWritePayload(change, {
      crNumber: trimmedCr,
      submissionStatus: 'Active'
    });

    // Backend validates cliConfig as a string. Encode values inside cliConfig before stringify.
    payload.cliConfig = this.encodeCliConfigForWrite(payload.cliConfig);

    // Guard: prevent creating a duplicate CR number.
    return this.getChangeByCrNumber(trimmedCr, { silentNotFound: true }).pipe(
      switchMap((existing: any) => {
        if (existing) {
          return throwError(() => new Error('DUPLICATE_CR_NUMBER'));
        }

        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this._http.post(`${this.url}/enac/change/`, payload, { headers });
      }),
      map((res: any) => res),
      catchError((error) => {
        console.error('Error creating change item:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Submits a change by updating its submissionStatus.
   * @param change The change object to be submitted
   */
  submitChange(change: any): Observable<any> {
    console.log('Submitting change:', change);
    change.submissionStatus = 'Submitted';
    return this.editChange(change);
  }

  /**
   * Cancels a change by updating its submissionStatus.
   * @param change The change object to be cancelled
   */
  cancelChange(change: any) {
    console.log('Cancelling change:', change);
    change.submissionStatus = 'Cancelled';

    return this.editChange(change);
  }

  /**
   * Closes a change by updating its submissionStatus.
   * @param change The change object to be closed
   */
  closeChange(change: any) {
    console.log('Closing change:', change);
    change.submissionStatus = 'Closed';
    return this.editChange(change);
  }

  activeChange(change: any) {
    console.log('Activating change:', change);
    change.submissionStatus = 'Active';
    return this.editChange(change);
  }

  /**
   * Edits an existing change in the backend with the provided change object.
   * @param change The change object to be edited
   * @returns An Observable containing the edited change or an error if the operation fails.
   */
  editChange(change: any) {
    console.log('Editing change:', change);

    const payload: any = this.buildChangeWritePayload(change);

    // Backend validates cliConfig as a string for edit. Encode values inside cliConfig before stringify.
    payload.cliConfig = this.encodeCliConfigForWrite(payload.cliConfig);

    const doPut = (p: any) =>
      this._http.put(this.url + '/enac/change/edit', p).pipe(
        catchError((error) => {
          console.error('Error editing change:', error);
          return throwError(() => error);
        }),
        map((res: any) => res)
      );

    // Defensive: some code paths construct a change object without configID.
    // If we have a CR number, fetch the backend's canonical row first to obtain configID.
    const hasId = payload?.configID !== undefined && payload?.configID !== null && payload?.configID !== '';
    const crNumber = (payload?.crNumber ?? '').toString().trim();
    if (hasId || !crNumber) {
      return doPut(payload);
    }

    return this.getChangeByCrNumber(crNumber, { silentNotFound: true }).pipe(
      switchMap((existing: any) => {
        const existingId = existing?.configID ?? existing?.configId ?? existing?.id;
        if (existingId === undefined || existingId === null || existingId === '') {
          // Let the backend respond with the most accurate error; avoid crashing in UI.
          return doPut(payload);
        }
        return doPut({ ...payload, configID: existingId });
      })
    );
  }

}
